import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';
import type {
  IExamRepository,
  CreateExamData,
  UpdateExamData,
  GetExamData,
  CreateExamAttemptData,
  GetExamAttemptData,
} from './interfaces/exam.repository.interface';

export class ExamRepository implements IExamRepository {
  private examsCollection = 'exams';
  private examAttemptsCollection = 'examAttempts';

  async createExam(examData: CreateExamData): Promise<GetExamData> {
    try {
      const colRef = collection(db, this.examsCollection);
      const docRef = await addDoc(colRef, {
        ...examData,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...examData,
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      } as GetExamData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_CREATE_FAILED',
        devMessage: `Failed to create exam "${examData.title}"`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getExam(examId: string): Promise<GetExamData | null> {
    try {
      const docRef = doc(db, this.examsCollection, examId);
      const docSnap = await getDoc(docRef);
      return docSnap.exists()
        ? ({ id: docSnap.id, ...docSnap.data() } as unknown as GetExamData)
        : null;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_GET_FAILED',
        devMessage: `Failed to get exam ${examId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getCourseExams(courseId: string): Promise<GetExamData[]> {
    try {
      const colRef = collection(db, this.examsCollection);
      const q = query(colRef, where('courseId', '==', courseId), where('isActive', '==', true));
      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as unknown as GetExamData));
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_GET_COURSE_EXAMS_FAILED',
        devMessage: `Failed to get exams for course ${courseId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async updateExam(examId: string, updateData: UpdateExamData): Promise<GetExamData> {
    try {
      const docRef = doc(db, this.examsCollection, examId);
      await updateDoc(docRef, {
        ...updateData,
        updatedAt: serverTimestamp(),
      });

      const updated = await getDoc(docRef);
      return { id: examId, ...updated.data() } as unknown as GetExamData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_UPDATE_FAILED',
        devMessage: `Failed to update exam ${examId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async deleteExam(examId: string): Promise<void> {
    try {
      const docRef = doc(db, this.examsCollection, examId);
      await deleteDoc(docRef);
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_DELETE_FAILED',
        devMessage: `Failed to delete exam ${examId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getInstructorExams(instructorId: string): Promise<GetExamData[]> {
    try {
      // Get courses by instructor
      const coursesRef = collection(db, 'certificationCourses');
      const coursesQuery = query(coursesRef, where('instructorId', '==', instructorId));
      const coursesSnapshot = await getDocs(coursesQuery);
      const courses = coursesSnapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Array<{ id: string; title?: string }>;

      if (courses.length === 0) return [];

      const courseIds = courses.map((c) => c.id);

      // Get exams for each course
      const examsRef = collection(db, this.examsCollection);
      const examPromises = courseIds.map(async (courseId) => {
        const q = query(examsRef, where('courseId', '==', courseId));
        const snapshot = await getDocs(q);
        return snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          courseName: courses.find((c) => c.id === courseId)?.title,
        }));
      });

      const allExams = await Promise.all(examPromises);
      return allExams.flat() as unknown as GetExamData[];
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_GET_INSTRUCTOR_EXAMS_FAILED',
        devMessage: `Failed to get exams for instructor ${instructorId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async createExamAttempt(attemptData: CreateExamAttemptData): Promise<GetExamAttemptData> {
    try {
      const colRef = collection(db, this.examAttemptsCollection);
      const docRef = await addDoc(colRef, {
        ...attemptData,
        startedAt: serverTimestamp(),
      });

      return {
        id: docRef.id,
        ...attemptData,
        startedAt: new Date(),
      } as GetExamAttemptData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_ATTEMPT_CREATE_FAILED',
        devMessage: `Failed to create exam attempt for user ${attemptData.userId}, exam ${attemptData.examId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async updateExamAttempt(
    attemptId: string,
    updates: Partial<GetExamAttemptData>,
  ): Promise<GetExamAttemptData> {
    try {
      const docRef = doc(db, this.examAttemptsCollection, attemptId);
      await updateDoc(docRef, {
        ...updates,
        completedAt: serverTimestamp(),
      });

      const updated = await getDoc(docRef);
      return { id: attemptId, ...updated.data() } as unknown as GetExamAttemptData;
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_ATTEMPT_UPDATE_FAILED',
        devMessage: `Failed to update exam attempt ${attemptId}`,
        cause: error,
        level: 'error',
      });
    }
  }

  async getUserExamAttempts(userId: string, examId?: string): Promise<GetExamAttemptData[]> {
    try {
      const colRef = collection(db, this.examAttemptsCollection);
      let q = query(colRef, where('userId', '==', userId));

      if (examId) {
        q = query(q, where('examId', '==', examId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((d) => ({ id: d.id, ...d.data() } as unknown as GetExamAttemptData));
    } catch (error) {
      if (error instanceof DRXError) throw error;
      throw new DRXError({
        code: 'EXAM_ATTEMPT_GET_USER_ATTEMPTS_FAILED',
        devMessage: `Failed to get exam attempts for user ${userId}`,
        cause: error,
        level: 'error',
      });
    }
  }
}
